import { DiscoveryHost } from "./onvifScanInterfaces";

export interface ScanHostSummary {
  /** Hosts that answered ONVIF but rejected every credential supplied. */
  authFailedIps: string[];
  /** True when any device warned about a remaining-attempts / lockout counter. */
  lockoutRisk: boolean;
  /**
   * Hosts that accepted a credential but returned no RTSP stream address, so
   * they cannot be auto-added. Credentials are not the problem for these.
   */
  noStreamIps: string[];
}

/** ONVIF login worked but no profile carried a stream address. */
export const hostHasNoStream = (host: DiscoveryHost): boolean =>
  host.onvif_auth_status === "success" &&
  !(host.profiles || []).some((profile) => !!profile.rtsp_uri);

// Cameras that count failed logins say so in the SOAP fault, e.g.
// "Invalid username or password! You still have 12 attempt(s)."
const LOCKOUT_PATTERN = /attempt\(s\)|attempts remaining|locked/i;

/**
 * Summarise why discovered hosts did not become cameras.
 *
 * A scan can turn up dozens of hosts and yield a single camera, with the reason
 * — every other camera rejected the one credential that was supplied — spread
 * across individual rows of a long table. This pulls that out so the operator is
 * told to add credentials rather than concluding the site has one camera.
 */
export const summarizeScanHosts = (
  hosts: DiscoveryHost[]
): ScanHostSummary => {
  const authFailedIps: string[] = [];
  const noStreamIps: string[] = [];
  let lockoutRisk = false;

  (hosts || []).forEach((host) => {
    if (hostHasNoStream(host)) {
      noStreamIps.push(host.ip);
      return;
    }
    if (host.onvif_auth_status !== "auth_failed") {
      return;
    }
    authFailedIps.push(host.ip);
    if (host.onvif_error && LOCKOUT_PATTERN.test(host.onvif_error)) {
      lockoutRisk = true;
    }
  });

  return { authFailedIps, lockoutRisk, noStreamIps };
};
